import { useState, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Download, Search, CheckSquare, Square } from "lucide-react"
import { exportToCSV } from "../../utils/formatting"

export interface ExportColumn {
  key: string
  label: string
  defaultSelected?: boolean
}

interface ExportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  columns: ExportColumn[]
  data: Record<string, any>[]
  filename: string
}

export function ExportDialog({
  open,
  onOpenChange,
  title,
  description,
  columns,
  data,
  filename,
}: ExportDialogProps) {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() =>
    new Set(columns.filter(c => c.defaultSelected !== false).map(c => c.key))
  )
  const [search, setSearch] = useState("")

  const filteredColumns = useMemo(() => {
    if (!search.trim()) return columns
    const q = search.toLowerCase()
    return columns.filter(c =>
      c.label.toLowerCase().includes(q) || c.key.toLowerCase().includes(q)
    )
  }, [columns, search])

  const toggleColumn = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const selectAll = () => setSelectedKeys(new Set(columns.map(c => c.key)))
  const selectNone = () => setSelectedKeys(new Set())

  const handleExport = () => {
    const cols = columns.filter(c => selectedKeys.has(c.key)).map(c => c.key)
    if (cols.length === 0) return
    exportToCSV(data, filename, cols)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 shrink-0">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search columns..."
              className="pl-8 h-8 text-sm"
            />
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={selectAll}>
              All
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={selectNone}>
              None
            </Button>
          </div>
        </div>

        <div className="text-xs text-muted-foreground shrink-0">
          {selectedKeys.size} of {columns.length} columns selected
          <Badge variant="outline" className="ml-2 text-[10px]">{data.length} rows</Badge>
        </div>

        <ScrollArea className="h-[280px] min-h-0 shrink-0 border rounded-md">
          <div className="p-2 space-y-0.5">
            {filteredColumns.map(col => (
              <label
                key={col.key}
                className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer text-sm"
              >
                <Checkbox
                  checked={selectedKeys.has(col.key)}
                  onCheckedChange={() => toggleColumn(col.key)}
                  className="h-3.5 w-3.5"
                />
                <span className="flex-1 truncate">{col.label}</span>
                <span className="text-[10px] text-muted-foreground font-mono truncate max-w-[120px]">{col.key}</span>
              </label>
            ))}
            {filteredColumns.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">No matching columns</p>
            )}
          </div>
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-2 shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleExport} disabled={selectedKeys.size === 0}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function ExportButton({ onClick, label = "Export" }: { onClick: () => void; label?: string }) {
  return (
    <Button variant="outline" size="sm" onClick={onClick}>
      <Download className="h-4 w-4 mr-2" />
      {label}
    </Button>
  )
}
